import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getEvents, getEvent, rsvpEvent } from "@/lib/api";
import { ArrowLeft, Calendar as CalIcon, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useT } from "@/lib/t";

export function EventsList() {
  const { lang } = useLang();
  const t = useT();
  const [events, setEvents] = useState([]);
  useEffect(() => { getEvents({ lang }).then(setEvents); }, [lang]);

  return (
    <div data-testid="events-page">
      <section className="bg-ccc-plum text-ccc-ivory py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-ccc-gold uppercase tracking-[0.3em] text-xs mb-3">{t("events.eyebrow")}</div>
          <h1 className="font-serif text-5xl mb-4">{t("events.hero_title")}</h1>
          <p className="text-stone-300 max-w-2xl">{t("events.hero_body")}</p>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-6">
        {events.length === 0 && (
          <div className="md:col-span-2 text-stone-500 py-12 text-center" data-testid="events-empty">
            {t("events.empty")}
          </div>
        )}
        {events.map(e => (
          <Link key={e.id} to={`/take-action/events/${e.slug}`} data-testid={`event-${e.slug}`}
            className="bg-white border border-stone-200 p-8 hover:border-ccc-purple transition-colors group">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ccc-gold-dark mb-3">
              <CalIcon size={12}/> {new Date(e.starts_at).toLocaleDateString(lang === "es" ? "es-CO" : undefined, { dateStyle: "medium" })}
            </div>
            <h3 className="font-serif text-2xl text-ccc-plum group-hover:text-ccc-purple mb-3 leading-tight">{e.title}</h3>
            <p className="text-sm text-stone-700 line-clamp-3">{e.description}</p>
            <div className="flex items-center gap-2 text-xs text-stone-500 mt-4">
              <MapPin size={12}/> {e.location}
            </div>
          </Link>
        ))}
      </section>
    </div>
  );
}

export function EventDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const t = useT();
  const [e, setE] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: 0 });
  const [done, setDone] = useState(false);

  useEffect(() => { getEvent(slug, lang).then(setE).catch(() => setE(false)); }, [slug, lang]);

  const submit = async (ev) => {
    ev.preventDefault();
    try {
      await rsvpEvent(slug, { ...form, guests: Number(form.guests) || 0 });
      setDone(true);
      toast.success(lang === "es" ? "RSVP confirmado. Revise su correo." : "RSVP confirmed. Check your email.");
    } catch { toast.error(lang === "es" ? "Error al confirmar" : "RSVP failed"); }
  };

  if (e === false) return <div className="max-w-4xl mx-auto px-6 py-24 font-serif text-2xl">{t("common.not_found")}</div>;
  if (!e) return <div className="max-w-4xl mx-auto px-6 py-24">{t("common.loading")}</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-16 grid md:grid-cols-5 gap-10" data-testid="event-detail">
      <div className="md:col-span-3">
        <Link to="/take-action/events" className="text-ccc-purple text-sm inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={14}/> {lang === "es" ? "Todos los eventos" : "All events"}
        </Link>
        <h1 className="font-serif text-5xl text-ccc-plum leading-tight mb-4">{e.title}</h1>
        <div className="flex flex-col gap-2 text-sm text-stone-600 mb-6">
          <div className="inline-flex items-center gap-2"><CalIcon size={14}/> {new Date(e.starts_at).toLocaleString(lang === "es" ? "es-CO" : undefined)}</div>
          <div className="inline-flex items-center gap-2"><MapPin size={14}/> {e.location}</div>
        </div>
        <div className="gilded-divider mb-6"/>
        <p className="text-stone-800 leading-relaxed font-serif text-lg whitespace-pre-wrap">{e.description}</p>
      </div>
      <div className="md:col-span-2">
        <div className="bg-white border border-stone-200 p-6 sticky top-6">
          {done ? (
            <div data-testid="rsvp-done">
              <div className="text-emerald-600 font-serif text-2xl mb-2">{lang === "es" ? "Está registrado." : "You're registered."}</div>
              <p className="text-sm text-stone-700">{lang === "es" ? "Le enviaremos un recordatorio 48 horas antes." : "We'll email you a reminder 48 hours before."}</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="rsvp-form">
              <div className="font-serif text-xl text-ccc-plum mb-3">{t("events.rsvp_button")}</div>
              <input required placeholder={lang === "es" ? "Nombre" : "Name"} value={form.name} onChange={ev => setForm({...form, name: ev.target.value})}
                className="w-full px-3 py-2 border border-stone-300 mb-2 focus:outline-none focus:border-ccc-purple"/>
              <input required type="email" placeholder={lang === "es" ? "Correo" : "Email"} value={form.email} onChange={ev => setForm({...form, email: ev.target.value})}
                className="w-full px-3 py-2 border border-stone-300 mb-2 focus:outline-none focus:border-ccc-purple"/>
              <input placeholder={lang === "es" ? "Teléfono (opcional)" : "Phone (optional)"} value={form.phone} onChange={ev => setForm({...form, phone: ev.target.value})}
                className="w-full px-3 py-2 border border-stone-300 mb-2 focus:outline-none focus:border-ccc-purple"/>
              <input type="number" min="0" placeholder={lang === "es" ? "Invitados" : "Guests"} value={form.guests} onChange={ev => setForm({...form, guests: ev.target.value})}
                className="w-full px-3 py-2 border border-stone-300 mb-4 focus:outline-none focus:border-ccc-purple"/>
              <button className="w-full bg-ccc-purple text-white py-3 uppercase text-sm tracking-wider font-semibold hover:bg-ccc-purple-dark">
                {lang === "es" ? "Reservar Mi Lugar" : "Reserve My Spot"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
